import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FolderOpen, ChevronDown, Check, LayoutGrid, FolderInput, Users } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProjectStore } from '@/stores/projectStore';
import { useUIStore } from '@/stores/uiStore';
import { useGroupStore } from '@/stores/groupStore';
import { useBackend } from '@/services/backend';
import {
  sortProjects,
  groupByTeam,
  isProjectActive,
  openTerminalPaths,
  UNGROUPED_ID,
} from '@/lib/projectSort';
import { getProjectGroupId, setProjectGroupId } from '@/lib/projectSettings';
import ProjectActivityDot from '@/components/ProjectActivityDot';
import type { Project } from '@/types/tauri';
import type { TerminalSessionData } from '@/services/backend/IBackend';

const normPath = (p: string) => p.replace(/\\/g, '/').toLowerCase();

/** Small project icon: custom image, emoji, or a fallback folder. */
function ProjectGlyph({ project, className = 'w-5 h-5' }: { project?: Project | null; className?: string }) {
  const icon = project?.icon;
  if (icon?.startsWith('data:')) return <img src={icon} alt="" className={`${className} rounded object-cover shrink-0`} />;
  if (icon) return <span className="text-lg leading-none shrink-0">{icon}</span>;
  return <FolderOpen className={`${className} shrink-0 text-base-content/60`} />;
}

/**
 * The mobile top-level project switcher. Replaces the static project name in the
 * header with a tappable control that opens a full-width sheet of projects —
 * the phone equivalent of the desktop Projects panel. Switching a project just
 * navigates to its workspace route (the workspace remounts for that project).
 *
 * When the user has defined Teams, projects are shown grouped by team (each a
 * collapsible section with Active/Inactive inside) and each row gets a "move to
 * team" button (touch has no drag-and-drop). With no teams the sheet keeps its
 * original flat Active/Inactive layout.
 */
export default function MobileProjectSwitcher() {
  const navigate = useNavigate();
  const location = useLocation();
  const projects = useProjectStore((s) => s.projects);
  const loadProjects = useProjectStore((s) => s.loadProjects);
  const updateProject = useProjectStore((s) => s.updateProject);
  const sortMode = useUIStore((s) => s.projectSortMode);
  const sortDir = useUIStore((s) => s.projectSortDir);
  const collapsedTeams = useUIStore((s) => s.collapsedTeams);
  const toggleTeamCollapsed = useUIStore((s) => s.toggleTeamCollapsed);
  const groups = useGroupStore((s) => s.groups);
  const loadGroups = useGroupStore((s) => s.loadGroups);
  const backend = useBackend();
  const [open, setOpen] = useState(false);
  // Inactive section starts collapsed each time the switcher opens so the
  // projects you're actively working in (open terminal) are front-and-center.
  const [inactiveOpen, setInactiveOpen] = useState(false);
  // When set, the "move to team" picker is shown for this project id.
  const [assignFor, setAssignFor] = useState<string | null>(null);
  // The HOST's full session list (all projects). On a remote client the local
  // terminalStore only knows the projects THIS device has opened, so grouping
  // off it would mark most active projects inactive. Querying the host via
  // listSessions() gives the real set of projects with a running terminal.
  const [hostSessions, setHostSessions] = useState<TerminalSessionData[] | null>(null);

  // Make sure the lists are populated even if the desktop sidebar never mounted.
  useEffect(() => {
    if (projects.length === 0) void loadProjects();
    void loadGroups();
  }, [projects.length, loadProjects, loadGroups]);

  // Refresh the host's session list every time the switcher opens, so the
  // Active/Inactive split reflects what's actually running right now.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void (async () => {
      try {
        const sessions = await backend.terminal.listSessions();
        if (!cancelled) setHostSessions(sessions);
      } catch {
        if (!cancelled) setHostSessions(null); // fall back to the local store
      }
    })();
    return () => { cancelled = true; };
  }, [open, backend]);

  const currentId = location.pathname.startsWith('/workspace/')
    ? location.pathname.split('/workspace/')[1]?.split('/')[0] ?? null
    : null;
  const current = projects.find((p) => p.id === currentId) ?? null;

  // "Active" is derived from the HOST's session list when available (so a remote
  // client sees every running project, not just the ones it opened); otherwise
  // fall back to the local terminalStore.
  const localOpenPaths = openTerminalPaths();
  let isActive: (p: Project) => boolean;
  if (hostSessions) {
    const activeIds = new Set<string>();
    const activePaths = new Set<string>();
    for (const s of hostSessions) {
      if (s.status !== 'active') continue;
      if (s.projectId) activeIds.add(s.projectId);
      if (s.projectPath) activePaths.add(normPath(s.projectPath));
    }
    isActive = (p) => activeIds.has(p.id) || activePaths.has(normPath(p.root_path));
  } else {
    isActive = (p) => isProjectActive(p, localOpenPaths);
  }

  const sorted = sortProjects(projects.filter((p) => p.status !== 'archived'), sortMode, sortDir);
  const hasTeams = groups.length > 0;

  const go = (id: string) => {
    setOpen(false);
    if (id !== currentId) navigate(`/workspace/${id}`);
  };

  const assignToTeam = async (projectId: string, teamId: string | null) => {
    const p = projects.find((x) => x.id === projectId);
    setAssignFor(null);
    if (!p || getProjectGroupId(p.settings) === teamId) return;
    try {
      await updateProject(projectId, { settings: setProjectGroupId(p.settings, teamId) });
    } catch {
      /* store logs it; next open re-reads */
    }
  };

  const renderRow = (p: Project) => (
    <div key={p.id} className="flex items-center">
      <button
        onClick={() => go(p.id)}
        role="option"
        aria-selected={p.id === currentId}
        className="flex items-center gap-2.5 flex-1 min-w-0 px-3 py-2.5 text-sm hover:bg-base-200 text-left"
      >
        <span className="relative inline-flex flex-shrink-0">
          <ProjectGlyph project={p} className="w-5 h-5" />
          <ProjectActivityDot projectPath={p.root_path} />
        </span>
        <span className="truncate flex-1">{p.name}</span>
        {p.id === currentId && <Check className="w-4 h-4 text-primary shrink-0" />}
      </button>
      {hasTeams && (
        <button
          onClick={() => setAssignFor(p.id)}
          className="px-3 py-2.5 text-base-content/40 hover:text-base-content active:text-base-content"
          title="Move to team"
          aria-label="Move to team"
        >
          <FolderInput className="w-4 h-4" />
        </button>
      )}
    </div>
  );

  // One team section: collapsible header + Active then (collapsible) Inactive.
  const renderTeamSection = (key: string, name: string, color: string | null, active: Project[], inactive: Project[]) => {
    const collapsed = collapsedTeams.includes(key);
    const count = active.length + inactive.length;
    return (
      <div key={key}>
        <button
          onClick={() => toggleTeamCollapsed(key)}
          className="flex items-center gap-2 px-3 pt-2 pb-1 w-full text-left hover:bg-base-200"
          aria-expanded={!collapsed}
        >
          <ChevronDown className={`w-3.5 h-3.5 shrink-0 text-base-content/50 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
          <span className="w-2 h-2 rounded-full shrink-0 border border-base-content/20" style={{ backgroundColor: color ?? 'transparent' }} />
          <span className="text-[10px] font-bold uppercase tracking-wider text-base-content/60 truncate">{name}</span>
          <span className="text-[10px] text-base-content/40">({count})</span>
        </button>
        {!collapsed && (
          <div className="pl-1">
            {active.length > 0 && (
              <>
                <div className="flex items-center gap-2 px-3 pt-1 pb-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-500/80">Active ({active.length})</span>
                </div>
                {active.map(renderRow)}
              </>
            )}
            {inactive.length > 0 && (
              <>
                <div className="px-3 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-base-content/40">
                  Inactive ({inactive.length})
                </div>
                {inactive.map(renderRow)}
              </>
            )}
            {count === 0 && <div className="px-3 py-1.5 text-xs text-base-content/40 italic">No projects</div>}
          </div>
        )}
      </div>
    );
  };

  const assignProject = assignFor ? projects.find((p) => p.id === assignFor) ?? null : null;
  const assignCurrentTeam = getProjectGroupId(assignProject?.settings ?? null);

  return (
    <>
      <button
        onClick={() => setOpen((v) => { const next = !v; if (next) setInactiveOpen(false); return next; })}
        className="flex items-center gap-1.5 flex-1 min-w-0 px-1.5 py-1 rounded hover:bg-base-300 active:bg-base-300 transition-colors"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <ProjectGlyph project={current} className="w-5 h-5" />
        <span className="text-sm font-semibold truncate">{current?.name ?? 'Projects'}</span>
        <ChevronDown className={`w-4 h-4 opacity-60 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open &&
        createPortal(
          <div className="fixed inset-0 z-[9999]" onClick={() => setOpen(false)}>
            <div className="absolute inset-0 bg-black/40" />
            <div
              className="absolute inset-x-0 top-12 bg-base-100 border-b border-base-300 shadow-2xl max-h-[70vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              role="listbox"
            >
              <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-base-content/50 sticky top-0 bg-base-100">
                Switch project
              </div>
              {projects.length === 0 && (
                <div className="px-3 py-3 text-sm text-base-content/50">No projects yet.</div>
              )}

              {/* No teams → flat Active/Inactive (original layout). The split
                  uses the host-aware predicate so the phone's Active count is
                  correct (not just the projects this device opened). */}
              {!hasTeams && projects.length > 0 && (() => {
                const act = sorted.filter(isActive);
                const inact = sorted.filter((p) => !isActive(p));
                return (
                  <>
                    {act.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 px-3 pt-2 pb-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-500/80">Active ({act.length})</span>
                        </div>
                        {act.map(renderRow)}
                      </div>
                    )}
                    {inact.length > 0 && (
                      <div>
                        <button
                          onClick={() => setInactiveOpen((v) => !v)}
                          className="flex items-center gap-2 px-3 pt-2 pb-1 w-full text-left hover:bg-base-200"
                          aria-expanded={inactiveOpen}
                        >
                          <ChevronDown className={`w-3.5 h-3.5 shrink-0 text-base-content/40 transition-transform ${inactiveOpen ? '' : '-rotate-90'}`} />
                          <span className="w-1.5 h-1.5 rounded-full bg-base-content/30 shrink-0" />
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-base-content/40">Inactive ({inact.length})</span>
                        </button>
                        {inactiveOpen && inact.map(renderRow)}
                      </div>
                    )}
                  </>
                );
              })()}

              {/* Teams → one section per team, then Ungrouped. */}
              {hasTeams && projects.length > 0 &&
                groupByTeam(sorted, groups, isActive).map((bucket) =>
                  renderTeamSection(
                    bucket.group?.id ?? UNGROUPED_ID,
                    bucket.group?.name ?? 'Ungrouped',
                    bucket.group?.color ?? null,
                    bucket.active,
                    bucket.inactive,
                  ),
                )}

              <div className="border-t border-base-300">
                <button
                  onClick={() => { setOpen(false); navigate('/'); }}
                  className="flex items-center gap-2.5 w-full px-3 py-2.5 text-sm hover:bg-base-200 text-left text-base-content/70"
                >
                  <LayoutGrid className="w-4 h-4 shrink-0" />
                  <span>All projects…</span>
                </button>
              </div>
            </div>

            {/* Move-to-team picker (second sheet, over the switcher). */}
            {assignProject && (
              <div className="absolute inset-0 z-[10] flex items-end" onClick={(e) => { e.stopPropagation(); setAssignFor(null); }}>
                <div className="absolute inset-0 bg-black/40" />
                <div className="relative w-full bg-base-100 border-t border-base-300 rounded-t-2xl shadow-2xl max-h-[60vh] overflow-y-auto pb-[env(safe-area-inset-bottom)]" onClick={(e) => e.stopPropagation()}>
                  <div className="px-4 py-3 text-sm font-semibold border-b border-base-300 truncate">
                    Move “{assignProject.name}” to…
                  </div>
                  {groups.map((g) => (
                    <button key={g.id} onClick={() => void assignToTeam(assignProject.id, g.id)} className="flex items-center gap-3 w-full px-4 py-3 text-sm hover:bg-base-200 text-left">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0 border border-base-content/20" style={{ backgroundColor: g.color ?? 'transparent' }} />
                      <span className="flex-1 truncate">{g.name}</span>
                      {assignCurrentTeam === g.id && <Check className="w-4 h-4 text-primary shrink-0" />}
                    </button>
                  ))}
                  <button onClick={() => void assignToTeam(assignProject.id, null)} className="flex items-center gap-3 w-full px-4 py-3 text-sm hover:bg-base-200 text-left">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0 border border-base-content/20" />
                    <span className="flex-1 truncate text-base-content/70">Ungrouped</span>
                    {assignCurrentTeam === null && <Check className="w-4 h-4 text-primary shrink-0" />}
                  </button>
                  <div className="border-t border-base-300">
                    <button onClick={() => { setAssignFor(null); setOpen(false); useUIStore.getState().openSettingsTo('app-groups'); }} className="flex items-center gap-3 w-full px-4 py-3 text-sm hover:bg-base-200 text-left text-base-content/70">
                      <Users className="w-4 h-4 shrink-0" />
                      <span>Manage teams…</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
